/** @jsx jsx */

import { ComponentProps, Fragment, useState } from 'react';
import { format } from 'date-fns';

import { Icon } from '@zapier/zinnia-icons';

import {
  Checkbox,
  Grid,
  SkeletonText,
  Tooltip,
  TooltipFocusableTrigger,
  Table,
  TableBody,
  TableHead,
  TableHeadSortButton,
  TableRow,
  TableTd,
  TableTh,
  Text,
} from '@zapier/design-system';

import { css, jsx } from '@emotion/core';

import MultiSelectDropdown from 'app/developer-v3/components/MultiSelectDropdown';
import { toSentenceCase } from 'app/developer-v3/utils/text';
import { compareVersionStringsDesc } from 'app/developer-v3/utils/version';
import {
  AppStatusName,
  VersionLifecycleStatus,
} from 'app/developer-v3/types/statuses';

import ColumnActions from './ColumnActions';
import RowVersion from './RowVersion';
import { getVersionStatusLabel } from './utils';

import type { SemVer } from 'app/developer-v3/types/version';
import type { Version } from './types';

const versionLifecycleStatusItems: ComponentProps<
  typeof MultiSelectDropdown
>['items'] = Object.entries(VersionLifecycleStatus).map(([key, value]) => ({
  label: toSentenceCase(key),
  value,
}));

const Styles = {
  tooltipContainer: css`
    display: flex;
    align-items: center;
  `,
};

type Props = {
  integrationId: number;
  integrationStatus: AppStatusName;
  getMenuItems: (
    version: Version
  ) => ComponentProps<typeof ColumnActions>['items'];
  isLoading: boolean;
  versions: Version[];
  viewAppVersion: (appVersion: SemVer) => void;
};

export default function VersionsTable({
  integrationId,
  integrationStatus,
  getMenuItems,
  isLoading,
  versions,
  viewAppVersion,
}: Props) {
  const [isVisibleChangelog, setIsVisibleChangelog] = useState(true);
  const [isVisibleAuditLogs, setIsVisibleAuditLogs] = useState(false);
  const [
    selectedVersionLifecycleStatusItems,
    setSelectedVersionLifecycleStatusItems,
  ] = useState(
    versionLifecycleStatusItems.filter(
      item => item.value !== VersionLifecycleStatus.deprecated
    )
  );
  const [sortByColumnIndex, setSortByColumnIndex] = useState(0);
  const [isReversedSort, setIsReversedSort] = useState(false);

  const visibleVersions = versions.filter(version =>
    selectedVersionLifecycleStatusItems
      .map(selectedStatus => selectedStatus.value)
      .includes(version.lifecycle.status)
  );

  const sortedVersions = visibleVersions.sort((versionX, versionY) => {
    const valueX =
      sortByColumnIndex === 0
        ? versionX?.version
        : sortByColumnIndex === 1
        ? getVersionStatusLabel(versionX.lifecycle.status)
        : sortByColumnIndex === 2
        ? versionX?.userCount || 0
        : sortByColumnIndex === 3
        ? format(new Date(versionX?.updatedAt), 'yyyy-MM-dd')
        : sortByColumnIndex === 4
        ? versionX?.platformVersion
        : '';

    const valueY =
      sortByColumnIndex === 0
        ? versionY?.version
        : sortByColumnIndex === 1
        ? getVersionStatusLabel(versionY.lifecycle.status)
        : sortByColumnIndex === 2
        ? versionY?.userCount || 0
        : sortByColumnIndex === 3
        ? format(new Date(versionY?.updatedAt), 'yyyy-MM-dd')
        : sortByColumnIndex === 4
        ? versionY?.platformVersion
        : '';

    if (sortByColumnIndex === 0) {
      return compareVersionStringsDesc(String(valueX), String(valueY));
    }

    return typeof valueX === 'number'
      ? Number(valueY) - Number(valueX)
      : String(valueY)?.localeCompare(String(valueX), undefined, {
          numeric: true,
        });
  });

  if (isReversedSort) {
    sortedVersions.reverse();
  }

  const onSortBy = (columnIndex: number) => {
    if (sortByColumnIndex === columnIndex) {
      setIsReversedSort(!isReversedSort);
    }
    setSortByColumnIndex(columnIndex);
  };

  return (
    <Fragment>
      <Grid alignItems="center" columns="300px 1fr repeat(2, auto)" gap="5px">
        <Grid alignItems="center" columns="repeat(2, auto)" gap="5px">
          <Text tag="span" type="Title">
            Lifecycle filter:
          </Text>
          <MultiSelectDropdown
            emptyDropdownMessage="No options available"
            items={versionLifecycleStatusItems}
            itemsType="items"
            onChange={selectedVersionLifecycleStatusItemsNext =>
              setSelectedVersionLifecycleStatusItems(
                selectedVersionLifecycleStatusItemsNext
              )
            }
            placeholder="Select lifecycles"
            selectedItems={selectedVersionLifecycleStatusItems}
          />
        </Grid>
        <span />
        <Checkbox
          isSelected={isVisibleChangelog}
          onChange={() => setIsVisibleChangelog(!isVisibleChangelog)}
        >
          Show changelog
        </Checkbox>
        <Checkbox
          isSelected={isVisibleAuditLogs}
          onChange={() => setIsVisibleAuditLogs(!isVisibleAuditLogs)}
        >
          Show history
        </Checkbox>
      </Grid>
      <Table>
        <TableHead>
          <TableRow>
            <TableTh>
              <TableHeadSortButton onClick={() => onSortBy(0)}>
                Version
              </TableHeadSortButton>
            </TableTh>
            <TableTh>
              <TableHeadSortButton onClick={() => onSortBy(1)}>
                Status
              </TableHeadSortButton>
            </TableTh>
            <TableTh>
              <TableHeadSortButton onClick={() => onSortBy(2)}>
                <div css={Styles.tooltipContainer}>
                  <Grid
                    alignItems="center"
                    columns="repeat(2, max-content)"
                    gap="5px"
                  >
                    Activity
                    <Tooltip
                      allowMultiline={true}
                      content="Count of users and live Zaps currently on the version."
                    >
                      <TooltipFocusableTrigger>
                        <Icon name="alertInfoFill" size={16} />
                      </TooltipFocusableTrigger>
                    </Tooltip>
                  </Grid>
                </div>
              </TableHeadSortButton>
            </TableTh>
            <TableTh>
              <TableHeadSortButton onClick={() => onSortBy(3)}>
                Last updated
              </TableHeadSortButton>
            </TableTh>
            <TableTh>
              <TableHeadSortButton onClick={() => onSortBy(4)}>
                <div css={Styles.tooltipContainer}>
                  <Grid
                    alignItems="center"
                    columns="repeat(2, max-content)"
                    gap="5px"
                  >
                    Platform version
                    <Tooltip
                      allowMultiline={true}
                      content='The underlying version of the Zapier Platform which your integration runs on. For integration versions built using the UI, this is managed for you. For integration versions created by the CLI, this corresponds to your version&apos;s "zapier-platform-core" dependency.'
                    >
                      <TooltipFocusableTrigger>
                        <Icon name="alertInfoFill" size={16} />
                      </TooltipFocusableTrigger>
                    </Tooltip>
                  </Grid>
                </div>
              </TableHeadSortButton>
            </TableTh>
            <TableTh>{null}</TableTh>
          </TableRow>
        </TableHead>
        {isLoading ? (
          <TableBody>
            {Array(4).map(indexRow => (
              <TableRow key={indexRow}>
                {Array(6).map(indexTd => (
                  <TableTd key={indexTd}>
                    <SkeletonText lines={1} />
                  </TableTd>
                ))}
              </TableRow>
            ))}
          </TableBody>
        ) : (
          sortedVersions.length > 0 && (
            <TableBody>
              {sortedVersions.map(version => (
                <RowVersion
                  getMenuItems={getMenuItems}
                  integrationId={integrationId}
                  integrationStatus={integrationStatus}
                  isVisibleAuditLogs={isVisibleAuditLogs}
                  isVisibleChangelog={isVisibleChangelog}
                  key={version.version}
                  version={version}
                  viewAppVersion={viewAppVersion}
                />
              ))}
            </TableBody>
          )
        )}
      </Table>
    </Fragment>
  );
}
